import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Users, Copy, Zap, Flame, LogOut, Trophy, Target, RefreshCw, Pencil,
  Check, X, Crown, Download, Bell, UserMinus, Loader2, BadgeCheck, Briefcase,
  Swords, AlertTriangle,
} from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackPageEvent } from "@/lib/pageAnalytics";

export default function TeamPage() {
  const { user: currentUser, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [org, setOrg] = useState(undefined); // undefined = loading, null = no org
  const [orgName, setOrgName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [trend, setTrend] = useState([]);
  const [quote, setQuote] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [checkoutStatus, setCheckoutStatus] = useState(null); // null | polling | success

  const loadOrg = useCallback(async () => {
    const { data } = await api.get("/orgs/me");
    setOrg(data);
    if (data?.is_admin) {
      api.get("/orgs/trend").then((r) => setTrend(r.data)).catch(() => {});
      api.get("/orgs/premium-quote").then((r) => setQuote(r.data)).catch(() => {});
    }
  }, []);

  useEffect(() => { loadOrg(); }, [loadOrg]);

  // Handle return from Team Premium Stripe checkout
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (params.get("checkout") !== "success" || !sessionId) return;
    setCheckoutStatus("polling");
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setCheckoutStatus("success");
          toast.success("Team Premium activated for your whole team!");
          refreshUser();
          loadOrg();
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts >= 8) { setCheckoutStatus(null); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createOrg = async () => {
    if (!orgName.trim()) return;
    setBusy(true);
    try {
      await api.post("/orgs", { name: orgName.trim() });
      toast.success("Team created!");
      loadOrg();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create team");
    } finally {
      setBusy(false);
    }
  };

  const joinOrgAction = async () => {
    if (!joinCode.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orgs/join", { join_code: joinCode.trim() });
      toast.success(data.granted_premium ? "Joined the team — Team Premium unlocked for you!" : "Joined the team!");
      if (data.granted_premium) refreshUser();
      loadOrg();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not join team");
    } finally {
      setBusy(false);
    }
  };

  const leaveOrg = async () => {
    setBusy(true);
    try {
      await api.post("/orgs/leave");
      toast.success("You left the team");
      setOrg(null);
    } finally {
      setBusy(false);
    }
  };

  const copyJoinCode = () => {
    navigator.clipboard.writeText(org.join_code)
      .then(() => toast.success("Join code copied!"))
      .catch(() => toast.error("Could not copy — copy it manually"));
  };

  const regenerateCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orgs/regenerate-code");
      setOrg((o) => ({ ...o, join_code: data.join_code }));
      toast.success("New join code generated — the old code no longer works.");
    } finally {
      setBusy(false);
    }
  };

  const saveName = async () => {
    if (!nameDraft.trim()) return;
    try {
      await api.put("/orgs/name", { name: nameDraft.trim() });
      setOrg((o) => ({ ...o, name: nameDraft.trim() }));
      setEditingName(false);
      toast.success("Team name updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not rename team");
    }
  };

  const removeMember = async (userId, name) => {
    try {
      await api.delete(`/orgs/members/${userId}`);
      toast.success(`${name} removed from the team`);
      loadOrg();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove member");
    }
  };

  const nudgeInactive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orgs/nudge");
      toast.success(data.nudged_count > 0 ? `Nudged ${data.nudged_count} inactive member(s)!` : "No inactive members to nudge right now.");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/orgs/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${org.name.replace(/\s/g, "_")}_team_report.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export team report");
    }
  };

  const startTeamPremiumCheckout = async () => {
    setBusy(true);
    trackPageEvent("/team", "buy_now_click");
    try {
      const { data } = await api.post("/payments/checkout", { package_id: "team_premium", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  if (org === undefined) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="team-page-loading"><div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!org) {
    return (
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-12" data-testid="team-page-no-org">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="w-6 h-6 text-indigo-400" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">B2B / Corporate</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Team Mode</h1>
        <p className="text-sm text-slate-400 mt-2 mb-8">Enroll your company's cohort, track everyone's progress together, and unlock bulk Team Premium seats.</p>

        <div className="glass-panel rounded-2xl p-6 mb-6">
          <h2 className="font-heading font-semibold text-white mb-3">Create a Team</h2>
          <div className="flex gap-2">
            <input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g. Acme Corp Cohort 1" data-testid="team-create-name-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            <button onClick={createOrg} disabled={busy} data-testid="team-create-submit-btn" className="bg-indigo-500 hover:bg-indigo-400 text-background font-semibold text-sm px-4 py-2.5 rounded-lg disabled:opacity-50">Create</button>
          </div>
        </div>

        <div className="glass-panel rounded-2xl p-6">
          <h2 className="font-heading font-semibold text-white mb-3">Join a Team</h2>
          <div className="flex gap-2">
            <input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="Enter 6-character join code" maxLength={6} data-testid="team-join-code-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white uppercase tracking-widest focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            <button onClick={joinOrgAction} disabled={busy} data-testid="team-join-submit-btn" className="bg-purple-500 hover:bg-purple-400 text-white font-semibold text-sm px-4 py-2.5 rounded-lg disabled:opacity-50">Join</button>
          </div>
        </div>
      </div>
    );
  }

  const { team_stats, members, is_admin, premium_active, premium_seats, premium_seats_used } = org;
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="team-page-with-org">
      {checkoutStatus === "polling" && (
        <div className="mb-6 glass-panel rounded-xl p-4 flex items-center gap-3 border border-amber-400/30" data-testid="team-checkout-polling-banner">
          <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
          <span className="text-sm text-slate-300">Confirming your Team Premium payment...</span>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Building2 className="w-6 h-6 text-indigo-400" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">B2B / Corporate</span>
            {premium_active && (
              <span className="inline-flex items-center gap-1 bg-amber-400/15 text-amber-300 text-[10px] font-bold uppercase px-2 py-1 rounded-full" data-testid="team-premium-active-badge">
                <Crown className="w-3 h-3" /> Team Premium · {premium_seats_used}/{premium_seats} seats
              </span>
            )}
          </div>
          {editingName ? (
            <div className="flex items-center gap-2">
              <input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} autoFocus data-testid="team-name-edit-input"
                className="font-heading text-2xl font-semibold bg-white/5 border border-white/10 rounded-lg px-3 py-1 text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              <button onClick={saveName} data-testid="team-name-save-btn" className="text-emerald-400 hover:text-emerald-300"><Check className="w-5 h-5" /></button>
              <button onClick={() => setEditingName(false)} data-testid="team-name-cancel-btn" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-3xl font-semibold text-white" data-testid="team-name">{org.name}</h1>
              {is_admin && (
                <button onClick={() => { setNameDraft(org.name); setEditingName(true); }} data-testid="team-name-edit-btn" className="text-slate-400 hover:text-indigo-300">
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyJoinCode} data-testid="team-copy-join-code-btn" className="flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-lg px-3.5 py-2 text-sm text-white">
            <Copy className="w-3.5 h-3.5 text-indigo-400" /> {org.join_code}
          </button>
          {is_admin && (
            <button onClick={regenerateCode} disabled={busy} data-testid="team-regenerate-code-btn" title="Regenerate join code" className="flex items-center gap-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-slate-300 px-3 py-2 rounded-lg disabled:opacity-50">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={leaveOrg} disabled={busy} data-testid="team-leave-btn" className="flex items-center gap-1.5 text-xs font-semibold bg-red-500/15 hover:bg-red-500/25 text-red-400 px-3.5 py-2 rounded-lg">
            <LogOut className="w-3.5 h-3.5" /> Leave
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <div className="glass-panel rounded-xl p-4 text-center" data-testid="team-stat-members"><Users className="w-4 h-4 text-indigo-400 mx-auto mb-1" /><p className="font-heading text-xl font-bold text-white">{team_stats.total_members}</p><p className="text-[10px] text-slate-400">Members</p></div>
        <div className="glass-panel rounded-xl p-4 text-center" data-testid="team-stat-xp"><Zap className="w-4 h-4 text-[#FBBF24] mx-auto mb-1" /><p className="font-heading text-xl font-bold text-white">{team_stats.total_xp}</p><p className="text-[10px] text-slate-400">Total XP</p></div>
        <div className="glass-panel rounded-xl p-4 text-center" data-testid="team-stat-completion"><Target className="w-4 h-4 text-emerald-400 mx-auto mb-1" /><p className="font-heading text-xl font-bold text-white">{team_stats.avg_completion_pct}%</p><p className="text-[10px] text-slate-400">Avg Completion</p></div>
        <div className="glass-panel rounded-xl p-4 text-center" data-testid="team-stat-streaks"><Flame className="w-4 h-4 text-indigo-400 mx-auto mb-1" /><p className="font-heading text-xl font-bold text-white">{team_stats.active_streaks}</p><p className="text-[10px] text-slate-400">Active Streaks</p></div>
        <div className="glass-panel rounded-xl p-4 text-center" data-testid="team-stat-inactive"><AlertTriangle className="w-4 h-4 text-red-400 mx-auto mb-1" /><p className="font-heading text-xl font-bold text-white">{team_stats.inactive_count}</p><p className="text-[10px] text-slate-400">Inactive</p></div>
      </div>

      {is_admin && (
        <div className="glass-panel rounded-2xl p-5 mb-6" data-testid="team-trend-chart-panel">
          <h2 className="font-heading font-semibold text-white text-sm mb-3">Team Progress — Last 14 Days</h2>
          <div className="h-[140px] min-h-[140px]" style={{ minWidth: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend}>
                <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="completed" name="Challenges Completed" fill="#6366F1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {is_admin && !premium_active && quote && (
        <div className="glass-panel rounded-2xl p-6 mb-6 border border-amber-400/30 relative overflow-hidden" data-testid="team-premium-upsell-card">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 to-transparent" />
          <div className="relative flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-heading font-semibold text-white flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> Unlock Team Premium</h3>
              <p className="text-xs text-slate-400 mt-1 max-w-md">Give your whole team unlimited AI mock interviews + a Premium badge for ${quote.unit_price}/seat one-time — that's {quote.seats} seat{quote.seats > 1 ? "s" : ""} for <span className="text-amber-300 font-semibold">${quote.total}</span> today (auto-covers future joiners too).</p>
            </div>
            <button onClick={startTeamPremiumCheckout} disabled={busy} data-testid="team-buy-premium-btn" className="bg-amber-400 hover:bg-amber-300 text-background font-bold text-sm px-5 py-2.5 rounded-xl disabled:opacity-50 shrink-0">
              Buy Team Premium
            </button>
          </div>
        </div>
      )}

      {is_admin && (
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button onClick={nudgeInactive} disabled={busy} data-testid="team-nudge-inactive-btn" className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white text-xs font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
            <Bell className="w-3.5 h-3.5 text-indigo-400" /> Nudge Inactive Members
          </button>
          <button onClick={exportCsv} data-testid="team-export-csv-btn" className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white text-xs font-semibold px-4 py-2.5 rounded-lg">
            <Download className="w-3.5 h-3.5 text-indigo-400" /> Export Team Report (CSV)
          </button>
        </div>
      )}

      <div className="glass-panel rounded-2xl divide-y divide-white/5" data-testid="team-members-list">
        {members.map((m, i) => (
          <div key={m.user_id} className="flex items-center gap-3 p-3.5 hover:bg-white/[0.03]" data-testid={`team-member-row-${m.user_id}`}>
            <span className="w-6 text-sm font-bold text-slate-400 shrink-0">{i === 0 ? <Trophy className="w-4 h-4 text-[#FBBF24]" /> : `#${i + 1}`}</span>
            <Link to={`/u/${m.user_id}`} className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-white overflow-hidden shrink-0">
              {m.picture ? <img src={m.picture} alt={m.name} loading="lazy" className="w-full h-full object-cover" /> : m.name?.[0]}
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <Link to={`/u/${m.user_id}`} className="text-sm text-white truncate hover:text-indigo-300">{m.name}</Link>
                {m.is_premium && <Crown className="w-3 h-3 text-amber-400 shrink-0" data-testid={`team-member-premium-badge-${m.user_id}`} />}
                {m.is_inactive && (
                  <span className="text-[9px] font-bold uppercase text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded shrink-0" data-testid={`team-member-inactive-badge-${m.user_id}`}>Inactive</span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-0.5 text-[10px] text-slate-400">
                <span className="flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> {m.cert_exams_passed} certs</span>
                <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {m.case_studies_completed} cases</span>
                <span className="flex items-center gap-1"><Swords className="w-3 h-3" /> {m.duel_wins} wins</span>
                {m.weakest_topic && <span className="truncate hidden sm:inline">Weak spot: {m.weakest_topic}</span>}
              </div>
            </div>
            <span className="text-xs text-slate-400 w-20 text-right shrink-0">{m.completion_pct}% done</span>
            <span className="text-xs font-bold text-[#FBBF24] w-16 text-right shrink-0">{m.xp} XP</span>
            {is_admin && m.user_id !== currentUser?.user_id && (
              <button onClick={() => removeMember(m.user_id, m.name)} data-testid={`team-remove-member-btn-${m.user_id}`} title="Remove from team" className="text-slate-400 hover:text-red-400 shrink-0">
                <UserMinus className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
